//#region Imports

import {
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {Subscription, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {LazyLoadEvent, SelectItem} from 'primeng/api';
import {DataView} from 'primeng/dataview';

import {
    ArticleDto,
    BasketArticleDto,
    CategoryDto
} from '@shared/service-proxies/service-proxies';

import {appModuleAnimation} from '@axilla/axilla-shared';

import { ViewMode } from '../../models';
import {WebShopBaseComponent} from '@app/webshop/shared/common/webshop-base.component';
import {DataViewService} from '@app/webshop/shared/services/data-view.service';
import {BasePermissionDefinition} from '@shared/permission/base-permission-definition';


//#endregion Imports

@Component({
    selector: 'app-data-view',
    templateUrl: './data-view.component.html',
    styleUrls: ['./data-view.component.less'],
    encapsulation: ViewEncapsulation.None,
    animations: [appModuleAnimation()]
})
export class DataViewComponent extends WebShopBaseComponent implements OnInit, OnDestroy {
    private _dataViewService: DataViewService;

    protected readonly BasePermissionDefinition = BasePermissionDefinition;

    @ViewChild('dv') dataView: DataView | undefined;

    get templateOptions() {
        return this._dataViewService.templateOptions;
    }
    // Selected template value
    selectedTemplate: string | null = 'theme1'; // Default is no selection

    @Output()
    onQuickAction: EventEmitter<any> = new EventEmitter();

    @Output()
    openDetails: EventEmitter<any> = new EventEmitter();
    @Input()
    hideSearch: boolean;

    //#region Items
    private _items: BasketArticleDto[] = [];

    get items(): BasketArticleDto[] {
        return this._items;
    }

    @Input()
    set items(items: BasketArticleDto[]) {
        // Add logic to transform or validate input here
        this._items = items ? items : [];

        this.primengTableHelper.records = items;
    }
    //#endregion Items

    @Input()
    category?: CategoryDto;
    categoryId: string;
    @Input()
    hideToolbar = false;

    //Flag which shows is search required
    @Input()
    isSortingAvailable = false;
    //Sorting option
    @Input()
    sortOptions: SelectItem[];
    //Sorting option

    @Input()
    sortOrder: number;

    @Input()
    sortField: string;

    @Output()
    searchAndLoadData: EventEmitter<any> = new EventEmitter();

    webshopForm: UntypedFormGroup;

    orderGuideId: string;

    isLoading = false;

    changeRouter$: Subscription;
    sortKey = '';

    isCategoryMode = false;
    protected readonly DataView = DataView;

    private viewMode$: Observable<ViewMode>;
    private currentPage$: Observable<number>;


    private currentTemplate$: Observable<string>;

    //private readonly searchTerm = new Subject();
    //private readonly searchTerm$ = this.searchTerm.asObservable().pipe(
    //    debounceTime(1000),
    //    concatMap(() => this.onSearchAndLoadData())
    //);

    //search() {
    //    this.searchTerm.next();
    //}

    //#region First
    private _first = 0;

    // Getter for the `total` input property
    get first(): number {
        return this._first;
    }

    set first(value: number) {
        this._first = value ? value : 0; // Default to an empty array if the value is falsy
    }
    //#endregion First

    constructor(
        injector: Injector,
    ) {
        super(injector);

        this._dataViewService = injector.get(DataViewService);

        this.viewMode$ = this._combineViewService.viewMode$;
        this.currentPage$ = this._combineViewService.currentPage$;
        this.currentTemplate$ = this._dataViewService.currentTemplate$;
    }

    get webshopCode() {
        if (this.webshopForm) {
            return this.webshopForm.get('code').value;
        }
        return '';
    }

    get webshopTitle() {
        if (this.webshopForm) {
            return this.webshopForm.get('title').value;
        }
        return '';
    }

    //#region Total Amount
    private _total = 0;

    // Getter for the `total` input property
    get total(): number {
        return this._total;
    }

    @Input()
    set total(value: number) {
        // Add logic to transform or validate input here
        this._total = value ? value : 0; // Default to an empty array if the value is falsy
    }
    //#endregion Total Amount

    //#region ViewMode
    private _localViewMode: ViewMode = ViewMode.ListView;

    get localViewMode(): ViewMode {
        return this._localViewMode;
    }

    set localViewMode(value: ViewMode) {
        this._localViewMode = value;
    }

    get viewMode(): ViewMode {
        return this._combineViewService.currentMode;
    }

    get layout(): 'list' | 'grid' {
        return this.localViewMode === ViewMode.ListView ? 'list' : 'grid';
    }
    //#endregion ViewMode

    onOpenDetails(item: any) {
        this.openDetails.emit(item);
    }

    toggleFavorites($event: Event, product: ArticleDto) {
        if (!product.favorite) {
            this._addToFavorite($event, product);
        } else {
            this._removeFromFavorite($event, product);
        }

        //reload list
        this.onSearchAndLoadData();
    }

    onSortChange(event) {
        let value = event.value;

        if (value.indexOf('!') === 0) {
            this.sortOrder = -1;
            this.sortField = value.substring(1, value.length);
        } else {
            this.sortOrder = 1;
            this.sortField = value;
        }
    }

    onSearchAndLoadData(event?: LazyLoadEvent) {
        let data = {
            event: event,
            webshopCode: this.webshopCode,
            webshopTitle: this.webshopTitle,
            primeng: this.primengTableHelper
        };

        this.searchAndLoadData.emit(data);
    }

    ngOnInit(): void {
        this.isLoading = true;

        this._initDataViewSerachForm();
        this._init();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private _init() {
        this.registerEvents();

        this.viewMode$
            .pipe(
                takeUntil(this.destroy$) // Automatically unsubscribes when destroy$ emits
            )
            .subscribe((viewMode) => {
                if (viewMode === ViewMode.TableView) {
                    return;
                }

                this.localViewMode = viewMode;
            });


        //init totalAmount
        this.currentPage$
            .pipe(
                takeUntil(this.destroy$) // Automatically unsubscribes when destroy$ emits
            )
            .subscribe((currentPage) => {
                this.currentPage = currentPage;

                this.first = currentPage === 0 ? 0 : (currentPage * this.rowsAmount);
            });

        this.currentTemplate$
            .pipe(
                takeUntil(this.destroy$) // Automatically unsubscribes when destroy$ emits
            )
            .subscribe((template) => {
                this.selectedTemplate = template;
            });
    }

    registerEvents(): void {
        abp.event.on('dv.reset', () => {
            if (this.dataView) {
                this.dataView.first = 0;
            }
        });
    }

    editLocalWarehouse(article: BasketArticleDto) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.permissionChecker.isGranted(BasePermissionDefinition.Webshop.WMS.EditLocalStock)) {
            const data = {
                article: article,
                isSelectingArticles: false
            };
            abp.event.trigger('app.show.editLocalWarehouseModal', data);
        }
    }

    addToBasket(article: BasketArticleDto) {
        this._addItemToBasket(article);
    }

    private _initDataViewSerachForm() {
        this.webshopForm = new UntypedFormGroup({
            code: new UntypedFormControl(null),
            title: new UntypedFormControl(null),
        });
    }

    isCategoriesAvailable(article: BasketArticleDto): boolean {
        return article.categories && article.categories.length > 0;
    }

    onClickQuickAction($event) {
        this.onQuickAction.emit($event);
    }

}

